import React, { useState } from 'react'

export default function ContactForm({ className = '', defaultService = '' }) {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    service: defaultService,
    message: '',
    website_url: '', // Honeypot field - must be present and empty for human submissions
  })

  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Venligst indtast dit fulde navn.'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Venligst indtast din emailadresse.'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Indtast venligst en gyldig emailadresse.'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Venligst indtast dit telefonnummer.'
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Venligst skriv en besked om dit bygge- eller graveprojekt.'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError('')

    if (!validate()) return

    // If honeypot is filled, silent reject to fool spam bots
    if (formData.website_url) {
      setSubmitted(true)
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.full_name.trim(),
          phone: formData.phone.trim(),
          email: formData.email.trim(),
          service: formData.service || undefined,
          message: formData.message.trim(),
          website_url: formData.website_url, // required honeypot
        }),
      })

      if (response.ok) {
        setSubmitted(true)
        setFormData({
          full_name: '',
          phone: '',
          email: '',
          service: defaultService,
          message: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => null)
        setSubmitError(
          errData?.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring på 55 23 69 32.'
        )
      }
    } catch (err) {
      console.error('Contact submission error:', err)
      setSubmitError('Kunne ikke få forbindelse til serveren. Tjek din internetforbindelse eller ring direkte til os på 55 23 69 32.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className={`rounded-2xl bg-emerald-50 border border-emerald-200 p-8 sm:p-10 text-center ${className}`}>
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 mb-4 shadow-inner">
          <i className="fa-solid fa-check text-2xl" aria-hidden="true" />
        </div>
        <h3 className="text-2xl font-bold text-emerald-950 mb-2">
          Tak for din henvendelse!
        </h3>
        <p className="text-emerald-800 text-sm max-w-md mx-auto mb-6">
          Vi har modtaget din besked og vender tilbage hurtigst muligt med et svar eller et uforpligtende tilbud på dit projekt.
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition"
        >
          <span>Send en ny besked</span>
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className={`relative bg-white rounded-2xl p-6 sm:p-8 lg:p-10 border border-slate-200 shadow-xl ${className}`}
    >
      {/* Required Honeypot field for anti-spam (absolute positioning + sr-only) */}
      <div className="sr-only absolute -left-[9999px] -top-[9999px] select-none pointer-events-none" aria-hidden="true">
        <label htmlFor="website_url_field">Website URL</label>
        <input
          id="website_url_field"
          type="text"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="space-y-5">
        {/* Full Name */}
        <div>
          <label htmlFor="full_name" className="block text-sm font-semibold text-slate-900 mb-1.5">
            Fuldt navn <span className="text-red-600">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              id="full_name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="F.eks. Anders Jensen"
              className={`w-full rounded-xl border bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 transition ${
                errors.full_name
                  ? 'border-red-400 focus:ring-red-500'
                  : 'border-slate-300 focus:border-red-600 focus:ring-red-600/20'
              }`}
            />
          </div>
          {errors.full_name && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              <span>{errors.full_name}</span>
            </p>
          )}
        </div>

        {/* Contact fields row: Phone & Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-1.5">
              Telefonnummer <span className="text-red-600">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="F.eks. 55 23 69 32"
              className={`w-full rounded-xl border bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 transition ${
                errors.phone
                  ? 'border-red-400 focus:ring-red-500'
                  : 'border-slate-300 focus:border-red-600 focus:ring-red-600/20'
              }`}
            />
            {errors.phone && (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span>{errors.phone}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-1.5">
              Email <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="F.eks. anders@email.dk"
              className={`w-full rounded-xl border bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 transition ${
                errors.email
                  ? 'border-red-400 focus:ring-red-500'
                  : 'border-slate-300 focus:border-red-600 focus:ring-red-600/20'
              }`}
            />
            {errors.email && (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>
        </div>

        {/* Optional Service Selection */}
        <div>
          <label htmlFor="service" className="block text-sm font-semibold text-slate-900 mb-1.5">
            Ønsket ydelse (valgfri)
          </label>
          <select
            id="service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="w-full rounded-xl border border-slate-300 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/20 transition"
          >
            <option value="">-- Vælg en ydelse hvis relevant --</option>
            <option value="Jordboring">Jordboring</option>
            <option value="Grøfterensning">Grøfterensning</option>
            <option value="Belægningsarbejde">Belægningsarbejde</option>
            <option value="Kloakarbejde">Kloakarbejde</option>
            <option value="Anlægsarbejde">Anlægsarbejde</option>
            <option value="Drænarbejde">Drænarbejde</option>
            <option value="Gravning og udgravning">Gravning og udgravning</option>
            <option value="Funderingsarbejde">Funderingsarbejde</option>
            <option value="Jordafgravning og nivellering">Jordafgravning og nivellering</option>
            <option value="Andet gravearbejde">Andet gravearbejde</option>
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-slate-900 mb-1.5">
            Besked / Beskrivelse af projektet <span className="text-red-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleChange}
            placeholder="Beskriv kort hvad du skal have lavet, hvor på Fyn det skal udføres, og hvornår du ønsker opstart..."
            className={`w-full rounded-xl border bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 transition ${
              errors.message
                ? 'border-red-400 focus:ring-red-500'
                : 'border-slate-300 focus:border-red-600 focus:ring-red-600/20'
            }`}
          />
          {errors.message && (
            <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              <span>{errors.message}</span>
            </p>
          )}
        </div>

        {/* Server Submit Error */}
        {submitError && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-xs text-red-800 flex items-start gap-2">
            <i className="fa-solid fa-triangle-exclamation text-red-600 mt-0.5" aria-hidden="true" />
            <span>{submitError}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-4 text-base font-bold text-white transition duration-200 hover:bg-red-700 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {submitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <span>Sender din besked...</span>
            </>
          ) : (
            <span>Indsend henvendelse</span>
          )}
        </button>

        <p className="text-center text-xs text-slate-500 pt-1">
          Vi svarer hurtigst muligt. Du kan også altid kontakte os direkte på tlf.{' '}
          <a href="tel:+4555236932" className="text-red-600 font-semibold hover:underline whitespace-nowrap">
            55 23 69 32
          </a>
        </p>
      </div>
    </form>
  )
}
